import { useTranslation } from "react-i18next";
import { cn } from "@/utils/cn";
import { Check, Globe } from "lucide-react";
import type { IAdvisor } from "../types";

interface AdvisorChipProps {
  advisor: IAdvisor;
  isActive: boolean;
  onSelect: (slug: string) => void;
  /** Roving tabindex — only the checked chip is in the tab order. */
  tabIndex: number;
  onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
}

/** One chip in the greeting-screen advisor row. Radio semantics, not a toggle. */
export const AdvisorChip = ({
  advisor,
  isActive,
  onSelect,
  tabIndex,
  onKeyDown,
}: AdvisorChipProps) => {
  const { t } = useTranslation(["common"]);
  const Icon = advisor.icon;

  return (
    <button
      type="button"
      role="radio"
      aria-checked={isActive}
      tabIndex={tabIndex}
      onKeyDown={onKeyDown}
      onClick={() => onSelect(advisor.slug)}
      title={t(advisor.tagline)}
      className={cn(
        "flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors",
        "max-sm:snap-start",
        "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
        isActive
          ? "border-primary bg-primary-muted text-primary"
          : "border-border bg-surface text-muted-foreground hover:border-primary/40 hover:text-foreground",
      )}
    >
      <Icon className="size-3.5" aria-hidden="true" />
      {t(advisor.name)}
      {advisor.webEnabled && (
        <Globe
          className={cn("size-3", isActive ? "text-primary" : "text-info")}
          aria-label={t("common:ai_assistant.searches_approved_sources")}
        />
      )}
      {isActive && <Check className="size-3.5" aria-hidden="true" />}
    </button>
  );
};
